import { useEffect, useRef } from 'react';
import { Dialog, DialogContent, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Embed from '@editorjs/embed';

export default function EditorJSModal() {
  const isOpen = useAppSelector((state: RootState) => state.ModalSlice.EditorJSModalOpen);
  const editorJSState = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditorJSModal);
  const dispatch = useAppDispatch();
  const ejInstance = useRef<EditorJS | null>(null);

  const getInitialData = () => {
    if (editorJSState.content) {
      return editorJSState.content;
    }
    
    const placeholderText = editorJSState.fieldType === 'description' 
      ? 'Start typing your event description...' 
      : 'Enter terms and conditions...';
    
    return {
      time: new Date().getTime(),
      blocks: [
        {
          type: 'paragraph',
          data: {
            text: placeholderText,
          },
        },
      ],
    };
  };

  useEffect(() => {
    if (isOpen && (!ejInstance.current || !ejInstance.current.isReady)) {
      const placeholderText = editorJSState.fieldType === 'description' 
        ? 'Start typing your event description...' 
        : 'Enter terms and conditions...';
        
      ejInstance.current = new EditorJS({
        holder: 'editorjs',
        autofocus: true,
        data: getInitialData(),
        placeholder: placeholderText,
        tools: {
          header: {
            class: Header,
            inlineToolbar: ['link', 'bold'],
          },
          list: {
            class: List,
            inlineToolbar: ['link', 'bold', 'italic'],
          },
          embed: {
            class: Embed,
          },
        },
        onChange: async () => {
          try {
            if (ejInstance.current) {
              const content = await ejInstance.current.saver.save();
              // eslint-disable-next-line no-console
              console.log(content);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to save content', e);
          }
        },
      });
    }

    return () => {
      if (ejInstance.current) {
        try {
          ejInstance.current.destroy();
        } catch (error) {
          console.error('Error destroying editor:', error);
        }
        ejInstance.current = null;
      }
    };
  }, [isOpen, editorJSState]);

  const handleSave = async () => {
    if (ejInstance.current) {
      try {
        const content = await ejInstance.current.saver.save();
        
        // Save content to the appropriate field based on source modal and field type
        if (editorJSState.sourceModal === 'AddEventsModal' || editorJSState.sourceModal === 'EditEventsModal') {
          // Dispatch action to save content back to the source modal state
          // This will be handled by a custom action in the component
          window.dispatchEvent(new CustomEvent('editorSaveContent', {
            detail: {
              content,
              fieldType: editorJSState.fieldType,
              sourceModal: editorJSState.sourceModal
            }
          }));
        }
        
        handleClose();
      } catch (error) {
        console.error('Failed to save content:', error);
      }
    }
  };

  const handleClose = () => {
    dispatch(closeModal('EditorJSModalOpen'));
  };

  return (
    <Dialog
      sx={{
        padding: 0,
        '& .MuiPaper-root': {
          borderRadius: '10px',
          width: '800px',
          maxWidth: '90vw'
        }
      }}
      maxWidth="lg"
      open={isOpen}
      PaperProps={{ component: 'div' }}
    >
      <DialogContent sx={{ paddingX: '24px', paddingY: '20px' }} className={'bg-white'}>
        <div className={'w-full'}>
          <div className={'header pb-3'}>
            <p className={'text-cf_heading font-[600]'}>
              {editorJSState.fieldType === 'description' ? 'Edit Event Description' : 'Edit Terms & Conditions'}
            </p>
          </div>

          <div className={'absolute right-4 top-4'}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className={'pt-2'}>
            <div className={'border border-gray-200 rounded-md'}>
              <div id="editorjs" style={{ minHeight: 300, padding: 12 }} />
            </div>
            
            <div className={'flex gap-2 pt-4 justify-end'}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  color: '#6B7280',
                  borderColor: '#6B7280',
                  '&:hover': {
                    backgroundColor: '#F3F4F6',
                    borderColor: '#6B7280',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#1D4ED8',
                  '&:hover': {
                    backgroundColor: '#1E40AF',
                  },
                }}
              >
                Save Content
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


